import { computed, nextTick, reactive, ref } from "vue";
import { defineStore } from "pinia";
import { useFlash } from "#ce/composables/useFlash";
import { createRsvpInviteApi } from "#ce/apps/SpAdmin/SpRsvpInviteApp/api/rsvpInvite";
import { isAcceptedFile, parseToken, pluralize, tokenize } from "#ce/apps/SpAdmin/SpRsvpInviteApp/utils/emailParser";

function defaultConfig() {
  return {
    eventId: 0,
    previewUrl: "",
    processUrl: "",
    addAttendeeUrl: "",
    label: "Attendee",
    emailInputLabel: "Type or paste email addresses",
    dropzoneText: "Drag and drop spreadsheet here, or <strong>click to browse</strong>",
    fileSpecHtml: "",
  };
}

function defaultPreviewData() {
  return {
    headers: [],
    rows: [],
    summary: {
      valid: 0,
      existing: 0,
      errors: 0,
      total: 0,
    },
    errors: [],
  };
}

export const navigation = {
  reloadPage() {
    window.location.reload();
  },
};

export const useRsvpInviteStore = defineStore("rsvpInvite", () => {
  const api = createRsvpInviteApi();
  const flash = useFlash();

  const config = reactive(defaultConfig());

  // STATE:
  const isOpen = ref(false);
  // Bumped once per modal open; keys the input step's dropzone so component-local
  // UI state (drag-over, file-spec panel) starts fresh each open.
  const openGeneration = ref(0);
  const currentStep = ref("input");
  const tags = ref([]);
  const inputText = ref("");
  const inviteeFile = ref(null);
  const sendInvites = ref(true);
  const isSubmitting = ref(false);
  const errorMessage = ref("");
  const previewData = ref(defaultPreviewData());
  const completedCount = ref(0);

  // COMPUTED:
  const labelPlural = computed(() => `${config.label}s`);
  const confirmDisabled = computed(() => isSubmitting.value || previewData.value.summary?.valid === 0);

  // ACTIONS:
  function configure(nextConfig = {}) {
    Object.assign(config, defaultConfig(), nextConfig);
  }

  function resetState() {
    currentStep.value = "input";
    tags.value = [];
    inputText.value = "";
    inviteeFile.value = null;
    sendInvites.value = true;
    isSubmitting.value = false;
    errorMessage.value = "";
    previewData.value = defaultPreviewData();
  }

  function open() {
    // Re-entrant opens (sp-modal re-emitting update-open) must not bump the
    // generation, or the input step would remount mid-session.
    if (isOpen.value) {
      return;
    }

    openGeneration.value += 1;
    isOpen.value = true;
  }

  function close() {
    isOpen.value = false;
    resetState();
  }

  function addTags(text) {
    const existing = new Set(tags.value.map((tag) => tag.email));
    const tokens = tokenize(text);

    for (const raw of tokens) {
      const parsed = parseToken(raw);
      if (parsed && !existing.has(parsed.email)) {
        tags.value.push(parsed);
        existing.add(parsed.email);
      }
    }
  }

  function removeTag(index) {
    tags.value.splice(index, 1);
  }

  function commitInput(text = inputText.value) {
    if (!text?.trim()) {
      return;
    }

    addTags(text);
    inputText.value = "";
  }

  function handleFileChange(file) {
    if (!file) {
      return;
    }

    if (!isAcceptedFile(file)) {
      errorMessage.value = "Unsupported file type. Please use .csv, .xlsx, or .xls files.";
      return;
    }

    inviteeFile.value = file;
    errorMessage.value = "";
  }

  function removeFile() {
    inviteeFile.value = null;
  }

  async function preview() {
    commitInput();

    if (tags.value.length === 0 && !inviteeFile.value) {
      errorMessage.value = "Please provide emails via text or file upload.";
      return false;
    }

    isSubmitting.value = true;
    errorMessage.value = "";

    const formData = new FormData();
    formData.append("event_id", config.eventId);

    if (inviteeFile.value) {
      formData.append("invitee_file", inviteeFile.value);
    }

    if (tags.value.length > 0) {
      formData.append("invitee_text", tags.value.map((tag) => tag.raw).join(", "));
    }

    try {
      const data = await api.preview(config.previewUrl, formData);

      previewData.value = {
        headers: data.headers || [],
        rows: data.rows || [],
        summary: data.summary || defaultPreviewData().summary,
        errors: data.errors || [],
      };
      currentStep.value = "confirm";
      return true;
    } catch (error) {
      errorMessage.value = error.message || "An error occurred while previewing. Please try again.";
      return false;
    } finally {
      isSubmitting.value = false;
    }
  }

  async function confirm() {
    isSubmitting.value = true;
    errorMessage.value = "";

    try {
      const data = await api.process(config.processUrl, {
        registrants: previewData.value.rows.filter((row) => row.valid).map((row) => row.values),
        send_invites: sendInvites.value,
        event_id: config.eventId,
      });

      const created = Number(data.created || 0);
      const skipped = Number(data.skipped || 0);
      const label = config.label.toLowerCase();
      let message = `${pluralize(created, label)} added${sendInvites.value ? " and invited" : ""}.`;

      if (skipped > 0) {
        message += ` ${skipped} skipped.`;
      }

      if (data.errors?.length > 0) {
        message += ` ${data.errors.join(". ")}`;
      }

      completedCount.value += 1;
      flash.success(message, { autoremoveDelay: 8000 });
      close();
      await nextTick();
      navigation.reloadPage();
      return true;
    } catch (error) {
      errorMessage.value = error.message || "An error occurred while processing. Please try again.";
      return false;
    } finally {
      isSubmitting.value = false;
    }
  }

  return {
    config,
    isOpen,
    openGeneration,
    currentStep,
    tags,
    inputText,
    inviteeFile,
    sendInvites,
    isSubmitting,
    errorMessage,
    previewData,
    completedCount,
    labelPlural,
    confirmDisabled,
    configure,
    open,
    close,
    addTags,
    removeTag,
    commitInput,
    handleFileChange,
    removeFile,
    preview,
    confirm,
  };
});
